import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DesktopMacIcon from "@mui/icons-material/DesktopMac";
import ImageIcon from "@mui/icons-material/Image";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideoLabelIcon from "@mui/icons-material/VideoLabel";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Grid,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import { format } from "date-fns";
import { enqueueSnackbar } from "notistack";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { extractJsonFromString, UseCreateGeminiRequest } from "0.Mine/1.Hooks/AI/Gemini";
import { useHandleSocialMedia } from "0.Mine/1.Hooks/CMS/SocialMedia";
import { UseMetricool } from "0.Mine/1.Hooks/social/metricool";
import { useHandleSocial } from "0.Mine/1.Hooks/social/social";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { Post } from "0.Mine/3.Interfaces/PostResponse";
import { SocialMediaIdentifier } from "0.Mine/3.Interfaces/social";
import { SafeArgonBox, SafeArgonButton } from "0.Mine/4.Argon/ArgonComponents";
import { searchImages } from "0.Mine/pexelsInstance";

import { DateTimePicker } from "./DateTimePicker";
import { Platform, PlatformSelector } from "./PlatformSelector";
import { PostEditor } from "./PostEditor";
import { useSocialPost } from "./PostOnSocialHook";
import { PreviewPane } from "./PreviewPane";

interface PostSchedulerProps {
  addPostToCalendar: (post: Post) => void;
}

export const PostScheduler: React.FC<PostSchedulerProps> = ({ addPostToCalendar }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { session } = useBusinessContext();
  const { createImageWithGemini, CreaSocialMediaPostWithGemini } = UseCreateGeminiRequest();
  const { getConnectedSocials } = UseMetricool();
  const { handlePost, isPosting } = useSocialPost();
  const [postContent, setPostContent] = useState<string>("");
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(["twitter"]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [mediaAttachments, setMediaAttachments] = useState<
    {
      id: string;
      url: string;
      type?: "image" | "video";
      file?: File;
    }[]
  >([]);
  const [previewImages, setPreviewImages] = useState<{ id: string; url: string }[]>([]);
  const [scheduleDate, setScheduleDate] = useState<Date>(new Date());
  const [scheduleTime, setScheduleTime] = useState<Date>(new Date());
  const [previewDevice, setPreviewDevice] = useState<"desktop" | "mobile">("desktop");
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [postMode, setPostMode] = useState<"now" | "schedule" | null>(null);

  // Move media checks to top-level memoization
  const hasVideos = useMemo(
    () => mediaAttachments.some((m) => m.type === "video"),
    [mediaAttachments]
  );
  const hasMedia = useMemo(() => mediaAttachments.length > 0, [mediaAttachments]);

  const getPlatformRequirements = (
    platformType: SocialMediaIdentifier
  ): {
    isValid: boolean;
    errorMessage: string | null;
    icon: any;
  } => {
    // Only log when debugging is enabled
    const DEBUG = false;
    if (DEBUG) {
      console.log("Validating with media attachments:", mediaAttachments);
    }

    // Use enum values for comparison
    switch (platformType) {
      case SocialMediaIdentifier.INSTAGRAM: {
        if (hasMedia) {
          return {
            isValid: true,
            errorMessage: hasVideos ? "Instagram videos should be under 60 seconds" : null,
            icon: hasVideos ? <VideoLabelIcon color="warning" fontSize="small" /> : null,
          };
        }
        return {
          isValid: false,
          errorMessage: "Instagram posts require at least one image or video",
          icon: <ImageIcon color="error" fontSize="small" />,
        };
      }

      case SocialMediaIdentifier.FACEBOOK: {
        const isWithinLimit = postContent.length <= 5000;
        return {
          isValid: isWithinLimit,
          errorMessage: isWithinLimit ? null : "Facebook posts must be under 5000 characters",
          icon: isWithinLimit ? null : (
            <Typography variant="caption" color="error">
              5000
            </Typography>
          ),
        };
      }

      case SocialMediaIdentifier.LINKEDIN: {
        const isWithinLimit = postContent.length <= 3000;
        return {
          isValid: isWithinLimit,
          errorMessage: isWithinLimit ? null : "LinkedIn posts must be under 3000 characters",
          icon: isWithinLimit ? null : (
            <Typography variant="caption" color="error">
              3000
            </Typography>
          ),
        };
      }

      default:
        return { isValid: true, errorMessage: null, icon: null };
    }
  };

  // Memoize enhanced platforms to prevent unnecessary recalculations
  const enhancedPlatforms = useMemo(
    () =>
      platforms.map((platform) => {
        const validation = getPlatformRequirements(platform.type);
        return {
          ...platform,
          isValid: validation.isValid,
          errorMessage: validation.errorMessage,
          validationIcon: validation.icon,
        };
      }),
    [platforms, postContent, hasMedia, hasVideos]
  ); // Update dependencies

  useEffect(() => {
    const fetchData = async () => {
      if (session?.selectedBusiness?.id) {
        const connectedSocials = await getConnectedSocials(session.selectedBusiness.id);
        if (!connectedSocials) return;

        // Filter out unsupported platforms
        const supportedPlatforms = connectedSocials.filter((platform) => {
          // Check if the platform.id is in the SocialMediaIdentifier enum
          return Object.keys(SocialMediaIdentifier)
            .map((key) => key.toLowerCase())
            .includes(platform.id.toLowerCase());
        });

        // Update the platform mapping code in fetchData
        const platformsData = supportedPlatforms.map((platform) => {
          // Convert string id to enum value
          const platformEnum =
            SocialMediaIdentifier[platform.id.toUpperCase() as keyof typeof SocialMediaIdentifier];

          const colorMap: { [key: string]: string } = {
            FACEBOOK: "#1877F2",
            INSTAGRAM: "#E1306C",
            TWITTER: "#1DA1F2",
            LINKEDIN: "#0077B5",
            YOUTUBE: "#FF0000",
          };

          return {
            id: platform.platformId,
            name: platform.username || platform.name,
            color: colorMap[platform.id.toUpperCase()] || "#000",
            type: platformEnum,
            image:
              platform.picture ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                platform.name
              )}&background=random`,
          } as Platform;
        });

        setPlatforms(platformsData);
      }
    };
    fetchData();
  }, [session?.selectedBusiness?.id, getConnectedSocials]);

  if (!session) return <Typography>Select a business to continue.</Typography>;

  const handlePlatformToggle = (id: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handlePostChange = (content: string) => setPostContent(content);
  const handleDeviceToggle = (d: "desktop" | "mobile") => setPreviewDevice(d);

  const toggleAttachment = (img: { id: string; url: string }) => {
    setMediaAttachments((prev) => {
      const exists = prev.find((m) => m.id === img.id);
      if (exists) return prev.filter((m) => m.id !== img.id);

      // When adding a Pexels image, add a type field
      const newMedia: { id: string; url: string; type: "image" | "video" } = {
        id: img.id,
        url: img.url,
        type: "image",
      };

      return [...prev, newMedia];
    });
  };

  const onGenerate = async (prompt: string, genImage: boolean) => {
    if (!session?.selectedBusiness) {
      enqueueSnackbar("Seleziona un'azienda per continuare", { variant: "error" });
      return;
    }

    //gen text
    if (prompt) {
      try {
        const resp2 = await CreaSocialMediaPostWithGemini({
          prompt: prompt,
          business: session.selectedBusiness,
          postType: "text",
          media: mediaAttachments.map((m) => m.url).join(","),
          content: postContent,
          scheduledDate: scheduleDate.toLocaleDateString(),
          restraints: "none",
          socialMedia: SocialMediaIdentifier.FACEBOOK,
        });

        console.log("Generated text:", resp2);
        if (resp2) {
          setPostContent(resp2);
        }
      } catch (error) {
        console.error("Error generating text:", error);
        enqueueSnackbar("Error generating text", { variant: "error" });
      }
    }

    //Gen image
    if (!genImage) return;
    try {
      const resp = await createImageWithGemini(
        session.selectedBusiness.id.toString(),
        prompt,
        postContent
      );
      const json = extractJsonFromString(resp || "");
      const query = json?.prompt || prompt;
      const results = await searchImages(query);
      const randomFive = results
        .sort(() => 0.5 - Math.random())
        .slice(0, 10)
        .map((p) => ({ id: p.id.toString(), url: p.src.large }));
      setPreviewImages(randomFive);
      setMediaAttachments([]); // reset selections
    } catch (error) {
      console.error("Error generating image:", error);
      enqueueSnackbar("Error generating image", { variant: "error" });
    }
  };

  const handleDatePickerOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setShowDatePicker(true);
  };
  const handleDatePickerClose = () => {
    setShowDatePicker(false);
    setAnchorEl(null);
  };

  // Handle file upload click
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Process each file
    Array.from(files).forEach((file) => {
      // Check if file is an image or video
      if (!file.type.match("image.*") && !file.type.match("video.*")) {
        enqueueSnackbar("File must be an image or video", { variant: "error" });
        return;
      }

      // Different size limits for images and videos
      const isVideo = file.type.match("video.*");
      const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024; // 50MB for videos, 5MB for images

      if (file.size > maxSize) {
        enqueueSnackbar(
          `${isVideo ? "Video" : "Image"} size must be less than ${isVideo ? "50MB" : "5MB"}`,
          { variant: "error" }
        );
        return;
      }

      // Create a blob URL for the file
      const fileUrl = URL.createObjectURL(file);

      // Add to media attachments with file type information
      const newMedia: { id: string; url: string; type: "image" | "video"; file: File } = {
        id: `upload-${Date.now()}-${file.name}`,
        url: fileUrl,
        file: file,
        type: isVideo ? "video" : "image",
      };

      setMediaAttachments((prev) => [...prev, newMedia]);
    });

    // Reset the input
    if (event.target) {
      event.target.value = "";
    }
  };

  const cleanupAttachments = () => {
    mediaAttachments.forEach((media) => {
      if (media.url && media.url.startsWith("blob:")) {
        URL.revokeObjectURL(media.url);
      }
    });
    setMediaAttachments([]);
  };

  const cleanMediaUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return `${urlObj.origin}${urlObj.pathname}`;
    } catch (e) {
      return url;
    }
  };

  const handleSchedule = async () => {
    try {
      console.log("=== Starting Post Scheduling Process ===");
      console.log("Current state:", {
        selectedPlatforms,
        postContent,
        mediaAttachments,
        scheduleDate: scheduleDate.toISOString(),
        scheduleTime: scheduleTime.toISOString(),
        businessId: session?.selectedBusiness?.id,
      });

      // Create a single Date object that combines selected date and time
      const scheduledDateTime = new Date(scheduleDate);

      // Set the hours, minutes, seconds from the time picker
      scheduledDateTime.setHours(scheduleTime.getHours(), scheduleTime.getMinutes(), 0, 0);

      console.log("Combined scheduled datetime:", scheduledDateTime.toISOString());

      // Ensure the time is actually in the future
      const now = new Date();
      if (scheduledDateTime <= now) {
        console.error("Invalid schedule time: Time is in the past");
        enqueueSnackbar("Please select a future date and time", { variant: "error" });
        return;
      }

      // Separate file uploads from URL-based images
      const fileAttachments = mediaAttachments.filter((m) => m.file).map((m) => m.file!);

      console.log(
        "File attachments:",
        fileAttachments.map((f) => ({ name: f.name, type: f.type, size: f.size }))
      );

      const urlAttachments = mediaAttachments
        .filter((m) => !m.file && m.url)
        .map((m) => cleanMediaUrl(m.url));

      console.log("URL attachments:", urlAttachments);

      console.log("Calling handlePost with params:", {
        businessId: session.selectedBusiness.id,
        postContent: postContent.substring(0, 50) + "...", // Log first 50 chars
        selectedPlatforms,
        platformsCount: platforms.length,
        fileAttachmentsCount: fileAttachments.length,
        urlAttachmentsCount: urlAttachments.length,
        scheduleDate: scheduledDateTime.toISOString(),
      });

      const result = await handlePost({
        businessId: session.selectedBusiness.id,
        postContent,
        selectedPlatforms,
        platforms,
        mediaAttachments: fileAttachments,
        imageUrls: urlAttachments,
        scheduleDate: scheduledDateTime.toISOString(),
        addPostToCalendar,
      });

      console.log("Post result:", result);

      if (result.success) {
        console.log("Post scheduled successfully, cleaning up...");
        cleanupAttachments();
        setPostContent("");
        setScheduleDate(new Date());
        setScheduleTime(new Date());
      }
    } catch (error) {
      console.error("Error in handleSchedule:", error);
      enqueueSnackbar("Failed to schedule post", { variant: "error" });
    }
  };

  // Add this new function to handle immediate posting
  const handlePostImmediately = async () => {
    // Separate file uploads from URL-based images
    const fileAttachments = mediaAttachments.filter((m) => m.file).map((m) => m.file!);

    const urlAttachments = mediaAttachments
      .filter((m) => !m.file && m.url)
      .map((m) => cleanMediaUrl(m.url)); // Apply cleanMediaUrl here

    const result = await handlePost({
      businessId: session.selectedBusiness.id,
      postContent,
      selectedPlatforms,
      platforms,
      mediaAttachments: fileAttachments,
      imageUrls: urlAttachments,
      addPostToCalendar,
    });

    if (result.success) {
      // Clean up all attachments including video blobs
      cleanupAttachments();
      setPostContent("");
    }
  };

  return (
    <SafeArgonBox
      elevation={3}
      sx={{
        width: "95vw",
        maxWidth: "100%",
        margin: "auto",
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      {/* Hidden file input */}
      <input
        type="file"
        accept="image/*, video/*"
        multiple
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Header */}
      <SafeArgonBox sx={{ p: 2 }}>
        <Typography variant="h5">Componi e Schedula</Typography>
      </SafeArgonBox>

      <Grid container sx={{ gap: 2, px: 2, pb: 2 }}>
        {/* Left */}
        <Grid
          item
          xs={12}
          lg={4}
          sx={{
            backgroundColor: isDark ? theme.palette.background.paper : "#fff",
            borderRadius: 3,
            boxShadow: isDark ? "0 0 0 1px rgba(255,255,255,0.09)" : "0 1px 4px rgba(0,0,0,0.1)",
          }}
        >
          <SafeArgonBox
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            {/* Platform selector */}
            <PlatformSelector
              platforms={enhancedPlatforms}
              selectedPlatforms={selectedPlatforms}
              onToggle={handlePlatformToggle}
            />

            {/* Content area - no fixed height */}
            <SafeArgonBox sx={{ mt: 2 }}>
              <PostEditor
                content={postContent}
                onChange={handlePostChange}
                onGenerate={onGenerate}
              />

              {/* Generated Image Preview Grid */}
              {previewImages.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Seleziona immagini</Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    {previewImages.map((img) => {
                      const selected = !!mediaAttachments.find((m) => m.id === img.id);
                      return (
                        <Box
                          key={img.id}
                          component="img"
                          src={img.url}
                          alt="preview"
                          onClick={() => toggleAttachment(img)}
                          sx={{
                            width: "100%",
                            height: 80,
                            objectFit: "cover",
                            borderRadius: 1,
                            border: selected
                              ? `2px solid ${theme.palette.primary.main}`
                              : "2px solid transparent",
                            cursor: "pointer",
                            transition: "border-color 0.3s",
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Selected Attachments Bar */}
              {mediaAttachments.length > 0 && (
                <SafeArgonBox sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1, pb: 1 }}>
                  {mediaAttachments.map((media) => (
                    <Box key={media.id} sx={{ position: "relative" }}>
                      {media.type === "video" ? (
                        <Box
                          component="video"
                          src={media.url}
                          controls={false}
                          muted
                          sx={{ width: 80, height: 80, objectFit: "cover", borderRadius: 1 }}
                        />
                      ) : (
                        <Box
                          component="img"
                          src={media.url}
                          alt="attachment"
                          sx={{ width: 80, height: 80, objectFit: "cover", borderRadius: 1 }}
                        />
                      )}
                      <IconButton
                        onClick={() => toggleAttachment(media)}
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          backgroundColor: "background.paper",
                          boxShadow: 1,
                        }}
                        size="small"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>

                      {/* Add video indicator */}
                      {media.type === "video" && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 4,
                            right: 4,
                            backgroundColor: "rgba(0,0,0,0.6)",
                            color: "white",
                            borderRadius: "50%",
                            width: 20,
                            height: 20,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <VideocamIcon sx={{ fontSize: 14 }} />
                        </Box>
                      )}
                    </Box>
                  ))}
                </SafeArgonBox>
              )}
            </SafeArgonBox>

            {/* Actions */}
            <SafeArgonBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 2,
                borderTop: 1,
                borderColor: "divider",
                pt: 2,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {/* Add upload button to the button group */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                {/* Carica media - full width */}
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleUploadClick}
                  color="secondary"
                  className="sfumatebtn"
                >
                  Carica media
                </Button>

                {/* Due bottoni al 50% */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<CalendarTodayIcon />}
                    onClick={handleDatePickerOpen}
                    className="sfumatebtn"
                    sx={{ flex: 1 }}
                  >
                    {scheduleDate
                      ? `${format(scheduleDate, "dd/MM/yyyy")} at ${format(scheduleTime, "HH:mm")}`
                      : "Data"}
                  </Button>
                  <SafeArgonButton
                    variant="outlined"
                    color="primary"
                    onClick={handlePostImmediately}
                    className="sfumatebtn-outline"
                    disabled={isPosting || !postContent || selectedPlatforms.length === 0}
                    sx={{
                      flex: 1,
                      "&.Mui-disabled": {
                        opacity: 0.2,
                      },
                      "&:hover:not(.Mui-disabled)": {
                        opacity: 1,
                      },
                    }}
                  >
                    {isPosting ? "Pubblicazione..." : "Pubblica entro 5 minuti"}
                  </SafeArgonButton>
                </Box>
              </Box>

              <SafeArgonButton
                variant="contained"
                color="success"
                sx={{ width: "100%" }}
                onClick={handleSchedule}
                disabled={
                  isPosting ||
                  !postContent ||
                  !scheduleDate ||
                  !scheduleTime ||
                  selectedPlatforms.length === 0
                }
              >
                {isPosting ? "Schedulazione..." : "Schedula post"}
              </SafeArgonButton>
            </SafeArgonBox>

            <Popover
              open={showDatePicker}
              anchorEl={anchorEl}
              onClose={handleDatePickerClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <DateTimePicker
                date={scheduleDate}
                time={scheduleTime}
                onDateChange={setScheduleDate}
                onTimeChange={setScheduleTime}
                onClose={handleDatePickerClose}
              />
            </Popover>
          </SafeArgonBox>
        </Grid>

        {/* Right Preview */}
        <Grid
          item
          xs={12}
          lg={7.8}
          sx={{
            backgroundColor: isDark ? theme.palette.grey[900] : theme.palette.grey[50],
            borderRadius: 3,
            minHeight: 500, // Minimum height for the preview panel
          }}
        >
          <SafeArgonBox
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="subtitle1" color="text.secondary">
              Anteprima
            </Typography>
            <ToggleButtonGroup
              value={previewDevice}
              exclusive
              onChange={(_, v) => v && handleDeviceToggle(v)}
              size="small"
            >
              <ToggleButton value="desktop">
                <DesktopMacIcon />
              </ToggleButton>
              <ToggleButton value="mobile">
                <SmartphoneIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </SafeArgonBox>
          <SafeArgonBox sx={{ p: 2, minHeight: 440 }}>
            {selectedPlatforms.length ? (
              <PreviewPane
                content={postContent}
                platforms={platforms}
                deviceType={previewDevice}
                media={mediaAttachments}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "text.secondary",
                }}
              >
                <Typography variant="h6">Nessuna piattaforma selezionata</Typography>
                <Typography variant="body2">
                  Seleziona almeno una piattaforma per l&lsquo; anteprima
                </Typography>
              </Box>
            )}
          </SafeArgonBox>
        </Grid>
      </Grid>
    </SafeArgonBox>
  );
};
